import { EventEmitter } from '@angular/core';
import { DataSource } from '@angular/cdk/table';
import { PaginatorComponent } from './paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

export class RestDataSource<T> implements DataSource<T> {
   private readonly dataObservable = new BehaviorSubject<T[]>([]);
   private _count = 0;
   private _q: string;
   private _filter: any;
   private _sort: MatSort | null;
   private _paginator: PaginatorComponent | null;
   private selection: SelectionModel<T>;
   changes = new EventEmitter<HttpParams>();

   constructor(_multiple = true) {
      this.selection = new SelectionModel<T>(_multiple, []);
   }

   connect(): BehaviorSubject<T[]> {
      return this.dataObservable;
   }

   disconnect(): void {
      this.dataObservable.complete();
      this.changes.complete();
   }

   get data() { return this.dataObservable.value; }
   get count() { return this._count; }

   get filter(): string { return this._q; }
   set filter(s: string) {
      if (this._q === s) { return; }
      this._q = s;
      if (this._paginator && this._sort) {
         this.loadData(true);
      }
   }

   setFilter(filter: any) {
      this._filter = filter;
      if (this._paginator && this._sort) {
         this.loadData(true);
      }
   }

   // get paginator(): Paginator | null { return this._paginator; }
   set paginator(paginator: PaginatorComponent | null) {
      paginator.pageChange.subscribe(() => this.loadData());
      this._paginator = paginator;
   }

  // get sort(): MatSort | null { return this._sort; }
   set sort(sort: MatSort | null) {
      sort.sortChange.subscribe(() => this.loadData(true));
      this._sort = sort;
   }

   loadData(resetPage = false): void {
      if (resetPage && this._paginator) {
         this._paginator.page = 0;
      }
      let params = new HttpParams();
      if (this._q) { params = params.set('searchText', this._q); }
      if (this._filter) {
         for (const key of Object.keys(this._filter)) {
            const value = this._filter[key];
            params = params.set(key, value);
         }
      }
      if (this._sort && this._sort.active && this._sort.direction) {
         let sort = this._sort.active;
         if (this._sort.direction === 'desc') { sort += ',desc'; }
         params = params.set('sort', sort);
      }
      if (this._paginator) {
         if (this._paginator.size) {
            if (this._paginator.page) {
               params = params.set('page', this._paginator.page.toString());
            }
            params = params.set('size', this._paginator.size.toString());
         }
      }
      this.changes.emit(params);
   }

   loaded(data: any): void {
      if (data.totalElements !== undefined) {
         this._count = data.totalElements;
      }
      this.dataObservable.next(data.content);
      this.selection.clear();
   }

   get selected(): T[] {
      return this.selection.selected;
   }

   hasSelection(): boolean {
      return this.selection.hasValue();
   }

   isSelected(value: T): boolean {
      return this.selection.isSelected(value);
   }

   isAllSelected(): boolean {
      return this.selection.selected.length === this.data.length;
   }

   selectAll(checked: boolean): void {
      checked ? this.data.forEach(item => this.selection.select(item)) : this.selection.clear();
   }

   clearSelection(): boolean | void {
      return this.selection.clear();
   }

   toggle(value: T): void {
      this.selection.toggle(value);
   }

   toggleAll() {
      this.isAllSelected() ?
         this.selection.clear() :
         this.data.forEach(item => this.selection.select(item));
   }

   removeSelected(): void {
      const data = this.data;
      for (const item of this.selected) {
         const index = data.indexOf(item);
         if (index !== -1) {
            data.splice(index, 1);
         }
      }
      this._count -= this.selected.length;
      this.dataObservable.next(data);
      this.selection.clear();
   }

}
